import { ObstetricsCalculator } from "@/Patient/Helpers/ObstetricsCalculator";
import { ObstetricalCounters } from "@/Patient/Models/MedicalSummary/ObstetricalSummary";
import { BaseObstetricalFileForm } from "../BaseObstetricalFileForm";
import { ObstetricalFileMedicalSummary } from "../ObstetricalFileMedicalSummary";
import { ObstetricalFileVitalSign } from "../ObstetricalFileVitalSign";
import { ObstetricalFileFormResources } from "../Resources/ObstetricalFileFormResources";
import { OntarioObstetricalFileFormCommonData } from "./OntarioObstetricalFileFormCommonData";
import { OntarioObstetricalFileFormOpr1 } from "./OntarioObstetricalFileFormOpr1";
import { OntarioObstetricalFileFormOpr2 } from "./OntarioObstetricalFileFormOpr2";
import { OntarioObstetricalFileFormOpr3 } from "./OntarioObstetricalFileFormOpr3";
import { OntarioObstetricalFileFormPostnatal } from "./OntarioObstetricalFileFormPostnatal";
import { OntarioObstetricalFileMetadata } from "./OntarioObstetricalFileMetadata";

class OntarioObstetricalFileForm extends BaseObstetricalFileForm {
    public Metadata: OntarioObstetricalFileMetadata;
    public Common: OntarioObstetricalFileFormCommonData;
    public Opr1: OntarioObstetricalFileFormOpr1;
    public Opr2: OntarioObstetricalFileFormOpr2;
    public Opr3: OntarioObstetricalFileFormOpr3;
    public Resources: ObstetricalFileFormResources;
    public Postnatal: OntarioObstetricalFileFormPostnatal;

    constructor(fields?: Partial<OntarioObstetricalFileForm>) {
        super(fields);
        this.ObstetricalCounters = new ObstetricalCounters(fields?.ObstetricalCounters);
        this.Opr1 = new OntarioObstetricalFileFormOpr1(fields?.Opr1);
        this.Opr2 = new OntarioObstetricalFileFormOpr2(fields?.Opr2);
        this.Opr3 = new OntarioObstetricalFileFormOpr3(fields?.Opr3);
        this.Common = new OntarioObstetricalFileFormCommonData(fields?.Common);
        this.Resources = new ObstetricalFileFormResources(fields?.Resources);
        this.Postnatal = new OntarioObstetricalFileFormPostnatal(fields?.Postnatal);
        this.Metadata = new OntarioObstetricalFileMetadata(fields?.Metadata);
    }

    public updateGestationalAgeAtBirth(): void {
        if (this.Common.FinalEstimatedDateOfBirth && this.Postnatal.DateOfDelivery)
            this.Postnatal.GestationalAgeAtBirth = ObstetricsCalculator.calculateWeeksOfGestation(this.Common.FinalEstimatedDateOfBirth, this.Postnatal.DateOfDelivery);
    }

    public updateAgeAtEstimatedDateOfBirth(patientDateOfBirth: Date, estimatedDateOfBirth: Date): void {
        const ageAtEstimatedDateOfBirth = ObstetricsCalculator.calculateAgeAtEstimatedDateOfBirth(patientDateOfBirth, estimatedDateOfBirth);

        if (ageAtEstimatedDateOfBirth)
            this.Opr1.PersonalInformation.AgeAtEstimatedDateOfBirth = ageAtEstimatedDateOfBirth;
    }

    public updateSubsequentVisitGestationalAge(contributionId: string): void {
        const currentVisit = this.Opr3.SubsequentVisits.firstOrDefault(visit => visit.ContributionId == contributionId);

        if (currentVisit) {
            currentVisit.GestationalAge = ObstetricsCalculator.calculateWeeksOfGestation(
                this.Common.FinalEstimatedDateOfBirth,
                currentVisit.Date
            );
        }
    }

    public setSubsequentVisitsVitalSigns(obstetricalFileMedicalSummary: ObstetricalFileMedicalSummary, consultationDate: Date, contributionCreationDate: Date, contributionId: string): void {
        const isVitalSignReused = this.isVitalSignReusedInOPR3();
        this.Opr3.SubsequentVisits.forEach((visit, idx) => {
            const emrTemplateId = visit.EmrTemplateId;
            const vitalSign = ObstetricalFileVitalSign.createObstetricalFileVitalSign({
                obstetricalFileMedicalSummary: obstetricalFileMedicalSummary,
                emrTemplateId: emrTemplateId,
                consultationDate: consultationDate,
                obstetricalFileContributionDate: contributionCreationDate,
                skipLoadLastHour: this.shouldSkipLastHourVitalSign(idx, emrTemplateId, obstetricalFileMedicalSummary, consultationDate) || visit.ContributionId !== contributionId
            });

            // if the vital sign selected was a reuse from OPR2 then updates the OPR2 vital sign too
            if (isVitalSignReused && idx === 0)
                this.Opr2.VitalSignEmrTemplateId = vitalSign?.emrTemplateId;

            visit.vitalSign = vitalSign;
            visit.EmrTemplateId = vitalSign?.emrTemplateId;
        });
    }

    public isVitalSignReusedInOPR3(): boolean {
        return !!this.Opr2.VitalSignEmrTemplateId
            && this.Opr3.SubsequentVisits?.any()
            && !!this.Opr3.SubsequentVisits[0].EmrTemplateId
            && this.Opr3.SubsequentVisits[0].EmrTemplateId === this.Opr2.VitalSignEmrTemplateId;
    }

    private shouldSkipLastHourVitalSign(idx: number, emrTemplateId: string, obstetricalFileMedicalSummary: ObstetricalFileMedicalSummary, consultationDate: Date): boolean {
        return (
            idx === 0 // if it is the first visit
            && !!this.Opr2.VitalSignEmrTemplateId // if there is a vital sign selected on OPR2
            && (emrTemplateId == null || // if there is no selected vital sign for the current visit
                emrTemplateId != this.Opr2.VitalSignEmrTemplateId || // if the vital sign selected for the current visit is not a reuse from OPR2
                obstetricalFileMedicalSummary.getVitalSignBy(emrTemplateId, consultationDate) !== null) // if the vital sign selected for the current visit is a reuse, but the vital sign doesn't exist anymore
        );
    }
}

export { OntarioObstetricalFileForm };
